import { lazy, type ComponentType, type LazyExoticComponent } from 'react'

import { loadRemote } from '@module-federation/enhanced/runtime'

import { Logger } from './logger'

/**
 * Asynchronously loads a remote module and returns the specified export.
 *
 * This function loads a remote module using `loadRemote` and attempts to retrieve the export defined by
 * `exportName` (which defaults to `'default'`). If the export is not found or an error occurs during
 * loading, an error is logged and the function returns `null`.
 *
 * @template T - The type of the expected export.
 * @param {string} moduleName - The name of the remote module to load.
 * @param {string} [exportName='default'] - The name of the export to retrieve from the remote module.
 * @returns {Promise<T | null>} A promise that resolves with the requested export, or `null` if not found or in case of error.
 */
export async function lazyRemoteModule<T>(
  moduleName: string,
  exportName: string = 'default'
): Promise<T | null> {
  try {
    const remoteModule = await loadRemote<{ [key: string]: T }>(moduleName)

    if (!remoteModule || !(exportName in remoteModule)) {
      throw new Error(`Failed to load remote module: "${moduleName}".`)
    }

    return remoteModule[exportName]
  } catch (error) {
    Logger.error(error.message)

    return null
  }
}

/**
 * Lazily loads a remote React component.
 *
 * The component is loaded using React.lazy. The remote module is expected to have a default export.
 * If an `exportName` is provided, the function returns the corresponding export from the module's default export;
 * otherwise, it uses the default export directly.
 *
 * @template Props - The type for the React component props.
 * @param {string} componentName - The name of the remote module containing the component.
 * @param {string} [exportName] - Optional. The name of the export to retrieve from the remote module's default export.
 *                                If not provided, the default export is used.
 * @returns {LazyExoticComponent<ComponentType<Props>>} A lazily loaded React component.
 * @throws {Error} Throws an error if the remote component fails to load.
 */
export function lazyRemote<Props extends Record<string, unknown>>(
  componentName: string,
  exportName: string = 'default'
): LazyExoticComponent<ComponentType<Props>> {
  return lazy(async function () {
    try {
      const RemoteComponent = await loadRemote<{ [key: string]: ComponentType<Props> }>(
        componentName
      )

      if (!RemoteComponent || !(exportName in RemoteComponent)) {
        throw new Error(`Failed to load remote component: ${componentName}.`)
      }

      return { default: RemoteComponent[exportName] }
    } catch (error) {
      Logger.error(error.message)

      return Promise.reject(error)
    }
  })
}
